import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Stack,
  Text,
  Title,
  Button,
  Group,
  Divider,
  ThemeIcon,
  Center,
} from "@mantine/core";
import { IconShieldLock } from "@tabler/icons-react";
import { PDFDocument, rgb } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import { sha256Hex } from "../lib/hash";
import {
  SIGNING_METADATA_FILENAME,
  createSigningMetadata,
  computeCreatorSigningInput,
  type SignerEntry,
  type FieldDefinition,
} from "../lib/signing-metadata";
import { SignatureAnnotation, FieldType } from "../types";
import { getStoredKeys, saveKey, signData, type KeyPair } from "../lib/keys";
import { UploadStep } from "./create-document/UploadStep";
import { FinalizeStep } from "./create-document/FinalizeStep";
import { EditorStep } from "./create-document/EditorStep";
import { generateIntroPage } from "../lib/pdf-intro";
import { generateMetaPage } from "../lib/meta-page";
import { loadPdfTheme } from "../lib/pdf-theme";
import { renderFieldToImage } from "../lib/pdf-utils";

// PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = "./pdf.worker.js";

const SIGNER_COLORS = [
  "#228be6", // blue
  "#40c057", // green
  "#fab005", // yellow
  "#fd7e14", // orange
  "#e64980", // pink
  "#be4bdb", // grape
  "#7950f2", // violet
];

type CreateDocumentViewProps = {
  annotations: SignatureAnnotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<SignatureAnnotation[]>>;
  initialFile?: File | null;
  onFileLoaded?: () => void;
  onNavigate?: (tab: string, file?: File | null) => void;
};

type ViewStep = "upload" | "editor" | "finalize" | "success";

export function CreateDocumentView({
  annotations,
  setAnnotations,
  initialFile,
  onFileLoaded,
  onNavigate,
}: CreateDocumentViewProps) {
  const storedKeys = getStoredKeys();

  const [step, setStep] = useState<ViewStep>("upload");

  // Document State
  const [pdfArrayBuffer, setPdfArrayBuffer] = useState<ArrayBuffer | null>(
    null,
  );
  const [metadata, setMetadata] = useState<{
    docTitle: string;
    author: string;
  }>({
    docTitle: "",
    author: "",
  });

  // Editor State
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed
  const [pdfDimensions, setPdfDimensions] = useState<
    { width: number; height: number }[]
  >([]);
  const [scale, setScale] = useState(0.8);
  const [pageThumbs, setPageThumbs] = useState<string[]>([]);
  const [pageImages, setPageImages] = useState<string[]>([]);
  /* Updated to include name */
  const [signers, setSigners] = useState<SignerEntry[]>([
    { name: "", email: "" },
  ]);
  const [selectedSignerIndex, setSelectedSignerIndex] = useState<string>("0");
  const [saving, setSaving] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{
    id: string;
    location: "canvas" | "sidebar";
  } | null>(null);

  // Signing State
  const [signingModalOpen, setSigningModalOpen] = useState(false);
  const [createKeyModalOpen, setCreateKeyModalOpen] = useState(false);
  const [availableKeys, setAvailableKeys] = useState<KeyPair[]>([]);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [passphrase, setPassphrase] = useState("");

  // Server Attestation State
  // Success State
  const [lastDownloadUrl, setLastDownloadUrl] = useState<string | null>(null);

  // Conflict state
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictFile, setConflictFile] = useState<File | null>(null);

  // Refs
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragImageRef = useRef<HTMLDivElement>(null);
  const activeColor =
    SIGNER_COLORS[parseInt(selectedSignerIndex) % SIGNER_COLORS.length] ||
    "#000";

  useEffect(() => {
    if (initialFile) {
      loadPdf(initialFile);
      if (onFileLoaded) onFileLoaded();
    }
  }, [initialFile]);

  // --------------------------------------------------------------------------
  // Step 1: Upload & Load
  // --------------------------------------------------------------------------
  const loadPdf = async (file: File) => {
    try {
      const arrayBuffer = await file.arrayBuffer();

      // Check if this is already a Signed+ document
      try {
        const pdfCheck = await pdfjsLib.getDocument({
          data: arrayBuffer.slice(0),
        }).promise;
        const attachments = await pdfCheck.getAttachments();
        if (attachments && attachments[SIGNING_METADATA_FILENAME]) {
          setConflictFile(file);
          setConflictModalOpen(true);
          return;
        }
      } catch (e) {
        // Ignore check errors, continue to normal load
      }

      setPdfArrayBuffer(arrayBuffer);

      const pdfDoc = await PDFDocument.load(arrayBuffer.slice(0));
      const importedTitle = pdfDoc.getTitle() || file.name.replace(".pdf", "");
      const importedAuthor = pdfDoc.getAuthor() || "";

      setMetadata({
        docTitle: importedTitle,
        author: importedAuthor,
      });

      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) })
        .promise;
      setNumPages(pdf.numPages);

      const thumbnails: string[] = [];
      const pages: string[] = [];
      const dimensions: { width: number; height: number }[] = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const vpThumb = page.getViewport({ scale: 0.25 });
        const canvasThumb = document.createElement("canvas");
        canvasThumb.width = vpThumb.width;
        canvasThumb.height = vpThumb.height;
        await page.render({
          canvasContext: canvasThumb.getContext("2d")!,
          viewport: vpThumb,
        }).promise;
        thumbnails.push(canvasThumb.toDataURL());

        const vpNative = page.getViewport({ scale: 1.0 });
        const vpRender = page.getViewport({ scale: 1.5 });
        const canvasPage = document.createElement("canvas");
        canvasPage.width = vpRender.width;
        canvasPage.height = vpRender.height;
        await page.render({
          canvasContext: canvasPage.getContext("2d")!,
          viewport: vpRender,
        }).promise;

        pages.push(canvasPage.toDataURL());
        dimensions.push({ width: vpNative.width, height: vpNative.height });
      }

      setPageThumbs(thumbnails);
      setPageImages(pages);
      setPdfDimensions(dimensions);
      setAnnotations([]);
      // Do NOT advance to editor yet, let the user fill out the title/author form first!
    } catch (e) {
      console.error(e);
      alert("Error loading PDF");
    }
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        setScale((s: number) => {
          const delta = -e.deltaY * 0.01;
          const newScale = Math.min(3.0, Math.max(0.1, s + delta));
          return newScale;
        });
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, []);

  const handleContinue = () => {
    if (!pdfArrayBuffer || !metadata.docTitle) return;
    setStep("editor");
    setScale(0.8);
  };

  // --------------------------------------------------------------------------
  // Editor Logic
  // --------------------------------------------------------------------------
  const addSigner = () => {
    setSigners((prev) => [...prev, { name: "", email: "" }]);
  };

  const removeSigner = (index: number) => {
    if (signers.length <= 1) return;
    setSigners((prev) => prev.filter((_, i) => i !== index));
    // Update annotations to remove deleted signer's fields or reassign?
    // For simplicity, just keep them but they might become invalid indices.
    // Better to remove annotations for that signer.
    setAnnotations((prev) => prev.filter((a) => a.signerId !== index));
  };

  const updateSignerName = (index: number, val: string) => {
    setSigners((prev) =>
      prev.map((s, i) => (i === index ? { ...s, name: val } : s)),
    );
  };

  const updateSignerEmail = (index: number, val: string) => {
    setSigners((prev) =>
      prev.map((s, i) => (i === index ? { ...s, email: val } : s)),
    );
  };

  const updateSigner = (index: number, updates: Partial<SignerEntry>) => {
    setSigners((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...updates } : s)),
    );
  };

  const addField = (type: FieldType, posMm?: { x: number; y: number }) => {
    const pageIdx = currentPage;
    const pageNum = pageIdx + 1;
    const dims = pdfDimensions[pageIdx];
    if (!dims) return;

    const widthMm = 35;
    const heightMm = type === "signature" ? 12 : 8;

    let x, y;
    if (posMm) {
      x = posMm.x;
      y = posMm.y;
    } else {
      const pdfWidthMm = (dims.width / 72) * 25.4;
      const pdfHeightMm = (dims.height / 72) * 25.4;
      x = (pdfWidthMm - widthMm) / 2;
      y = (pdfHeightMm - heightMm) / 2;
    }

    const isLabel = type === "label";
    let signerId: number | null = null;
    if (!isLabel) {
      const idx = parseInt(selectedSignerIndex, 10);
      signerId = isNaN(idx) ? 0 : idx;
    } else {
      signerId = null;
    }

    const newId = `${pageNum}-${type}-${Date.now()}`;
    const newAnn: SignatureAnnotation = {
      id: newId,
      page: pageNum,
      type,
      signerId,
      x,
      y,
      width: widthMm,
      height: heightMm,
      labelText: type === "label" ? "Label Text" : undefined,
    };

    setAnnotations((prev) => [...prev, newAnn]);
    setSelectedFieldId(newId);
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("fieldType") as FieldType;
    if (!type || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();

    const widthMm = 35;
    const heightMm = type === "signature" ? 12 : 8;
    const widthPx = widthMm * (72 / 25.4) * scale;
    const heightPx = heightMm * (72 / 25.4) * scale;

    const xPx = e.clientX - rect.left - widthPx / 2;
    const yPx = e.clientY - rect.top - heightPx / 2;

    const xMm = (xPx / scale) * (25.4 / 72);
    const yMm = (yPx / scale) * (25.4 / 72);

    addField(type, { x: xMm, y: yMm });
  };

  const onDragStop = (
    id: string,
    pageIdx: number,
    d: { x: number; y: number },
  ) => {
    const xMm = (d.x / scale) * (25.4 / 72);
    const yMm = (d.y / scale) * (25.4 / 72);
    setAnnotations((prev) =>
      prev.map((ann) => (ann.id === id ? { ...ann, x: xMm, y: yMm } : ann)),
    );
  };

  const onResizeStop = (
    id: string,
    pageIdx: number,
    ref: HTMLElement,
    position: { x: number; y: number },
  ) => {
    const wMm = (ref.offsetWidth / scale) * (25.4 / 72);
    const hMm = (ref.offsetHeight / scale) * (25.4 / 72);
    const xMm = (position.x / scale) * (25.4 / 72);
    const yMm = (position.y / scale) * (25.4 / 72);
    setAnnotations((prev) =>
      prev.map((ann) =>
        ann.id === id
          ? { ...ann, width: wMm, height: hMm, x: xMm, y: yMm }
          : ann,
      ),
    );
  };

  const savePreparedPdf = async (sigData: {
    keyId: string;
    signature: string;
    serverSignature: any;
    docHash: string;
    publicKey?: string;
  }) => {
    if (!pdfArrayBuffer) return;
    setSaving(true);
    try {
      const pdfDoc = await PDFDocument.load(pdfArrayBuffer);

      const fieldDefs: FieldDefinition[] = annotations.map((a) => ({
        id: a.id,
        type: a.type as any,
        page: a.page,
        x: a.x,
        y: a.y,
        width: a.width,
        height: a.height,
        signerIndex: a.signerId === null ? undefined : a.signerId,
        optional: a.optional,
        labelText: a.labelText,
      }));

      const meta = createSigningMetadata(
        sigData.docHash,
        signers, // Use all signers, even empty ones, to match indices
        fieldDefs,
        [], // signatures initial
      );

      // Add creator signature manually
      meta.creatorSignature = {
        keyId: sigData.keyId,
        signature: sigData.signature,
        serverSignature: sigData.serverSignature,
        publicKey: sigData.publicKey,
      };

      // Theme for intro and meta pages
      const theme = await loadPdfTheme(pdfDoc);

      // --- ADD INTRO PAGE ---
      await generateIntroPage(pdfDoc, {
        isComplete: false,
        documentTitle:
          metadata.docTitle || initialFile?.name || "Untitled Document",
        existingMetadata: meta,
        theme,
      });

      // --- ADD TAIL PAGE (INITIAL AUDIT LOG) ---
      await generateMetaPage(
        pdfDoc,
        {
          documentHash: sigData.docHash,
          isLastSigner: false,
          existingMetadata: meta,
          updatedSignatures: [],
          titleOverride: "Document Preparation Log",
          statusOverride: "Status: Prepared & Locked for Signing",
          statusColorOverride: { r: 0, g: 0.4, b: 0.8 },
        },
        {
          pdfDoc,
          ...theme,
          renderFieldToImage: (filled, pxW, pxH) =>
            renderFieldToImage(pdfDoc, filled, pxW, pxH),
        },
      );

      // --- ADJUST ANNOTATIONS PAGE INDEX ---
      // Since we added 1 page at the start, all fields that were on page N are now on page N+1
      const adjustedFields = fieldDefs.map((f) => ({ ...f, page: f.page + 1 }));
      meta.fields = adjustedFields;

      const finalMetaBytes = new TextEncoder().encode(
        JSON.stringify(meta, null, 2),
      );

      const pages = pdfDoc.getPages();
      // Draw placeholders on the original pages (offset by 1)
      for (const ann of annotations) {
        if (ann.page + 1 > pages.length) continue;
        const page = pages[ann.page]; // ann.page is 1-based index into original, but 0-based into pdfDoc.getPages()?
        // Wait, pdf-lib getPages() is 0-indexed.
        // Original page 1 was index 0. Now original page 1 is index 1.
        // So ann.page (1-based) is now the correct 0-based index for the actual page.

        const { height } = page.getSize();
        const xPts = ann.x * (72 / 25.4);
        const yPts = ann.y * (72 / 25.4);
        const wPts = ann.width * (72 / 25.4);
        const hPts = ann.height * (72 / 25.4);
        const finalY = height - yPts - hPts;

        const signerIndex = ann.signerId !== null ? ann.signerId : 0;
        const signerColors = [
          "#228be6", // blue
          "#12b886", // teal
          "#fab005", // yellow/gold
          "#fd7e14", // orange
          "#fa5252", // red
          "#be4bdb", // grape
          "#7950f2", // violet
        ];
        const signerColor = signerColors[signerIndex % signerColors.length];
        const hexToRgb = (hex: string) => {
          const clean = hex.replace("#", "");
          const r = parseInt(clean.substring(0, 2), 16) / 255;
          const g = parseInt(clean.substring(2, 4), 16) / 255;
          const b = parseInt(clean.substring(4, 6), 16) / 255;
          return rgb(r, g, b);
        };
        const borderRgb = hexToRgb(signerColor);
        const bgRgb = rgb(0.98, 0.98, 0.99);

        const drawRoundedRect = (pPage: any, rx: number, ry: number, rw: number, rh: number, rRadius: number, fillColor: any, borderCol: any, borderW: number) => {
          const R = Math.min(rRadius, rw / 2, rh / 2);
          const path = [
            `M ${R} 0`,
            `L ${rw - R} 0`,
            `A ${R} ${R} 0 0 1 ${rw} ${R}`,
            `L ${rw} ${rh - R}`,
            `A ${R} ${R} 0 0 1 ${rw - R} ${rh}`,
            `L ${R} ${rh}`,
            `A ${R} ${R} 0 0 1 0 ${rh - R}`,
            `L 0 ${R}`,
            `A ${R} ${R} 0 0 1 ${R} 0`,
            `Z`,
          ].join(" ");
          pPage.drawSvgPath(path, {
            x: rx,
            y: ry + rh,
            color: fillColor,
            borderColor: borderCol,
            borderWidth: borderW,
          });
        };

        // Draw rounded rectangle
        drawRoundedRect(page, xPts, finalY, wPts, hPts, 5, bgRgb, borderRgb, 1.25);

        // Center text label inside placeholder
        const text =
          ann.type === "label"
            ? ann.labelText || "Label"
            : `${ann.type.toUpperCase()} (Signer #${signerIndex + 1})`;
        const textWidth = theme.helvetica.widthOfTextAtSize(text, 8);
        const textHeight = 8;
        const tx = xPts + (wPts - textWidth) / 2;
        const ty = finalY + (hPts - textHeight) / 2;

        page.drawText(text, {
          x: tx,
          y: ty,
          size: 8,
          font: theme.helvetica,
          color: borderRgb,
        });
      }

      await pdfDoc.attach(finalMetaBytes, SIGNING_METADATA_FILENAME, {
        mimeType: "application/json",
        description: "Signing metadata",
      });
      await pdfDoc.attach(pdfArrayBuffer.slice(0), "original.pdf", {
        mimeType: "application/pdf",
        description: "Original document",
      });
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      // Store for retry download
      setLastDownloadUrl(url);

      // Auto-download
      const a = document.createElement("a");
      a.href = url;
      a.download = `${metadata.docTitle || "document"}-prepared.pdf`;
      a.click();

      // Transition to success step
      setStep("success");
    } catch (e) {
      console.error(e);
      alert("Failed to save PDF");
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------------------------------
  // Handlers
  // --------------------------------------------------------------------------

  const handleStartFinalize = () => {
    const keys = getStoredKeys();
    setAvailableKeys(keys);
    if (keys.length > 0) {
      setSelectedKeyId(keys[0].id);
    }
    setStep("finalize");
  };

  const handleGenerateClick = () => {
    if (availableKeys.length === 0) {
      setCreateKeyModalOpen(true);
    } else {
      setSigningModalOpen(true);
    }
  };

  const handleKeyCreated = (key: KeyPair) => {
    saveKey(key);
    setAvailableKeys(getStoredKeys());
    setSelectedKeyId(key.id);
    setCreateKeyModalOpen(false);
    // Proceed to signing immediately
    setSigningModalOpen(true);
  };

  const handleConfirmSign = async () => {
    const key = availableKeys.find((k) => k.id === selectedKeyId);
    if (!key) return;

    setSaving(true);
    try {
      const documentHash = await sha256Hex(pdfArrayBuffer!);

      // Build field definitions early so we can include them in the creator's signing input
      const fieldDefs: FieldDefinition[] = annotations.map((a) => ({
        id: a.id,
        type: a.type as any,
        page: a.page,
        x: a.x,
        y: a.y,
        width: a.width,
        height: a.height,
        signerIndex: a.signerId === null ? undefined : a.signerId,
        optional: a.optional,
        labelText: a.labelText,
      }));

      // 1. PGP-sign the full metadata (document hash + signers + fields)
      //    This binds the creator to the field layout and signer list
      const signingInput = computeCreatorSigningInput(
        documentHash,
        signers,
        fieldDefs,
      );
      const signature = await signData(
        signingInput,
        key.privateKeyArmored,
        passphrase || undefined,
      );

      // 2. Request server attestation (ECDSA-signed, replaces OTP)
      const attestResp = await fetch("/api/attest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: key.email,
          keyId: key.id,
          docHash: documentHash,
        }),
      });
      const attestData = await attestResp.json();
      if (!attestData.success) {
        throw new Error(attestData.error || "Server attestation failed");
      }

      // 3. Save the document with both signatures
      setSigningModalOpen(false);
      await savePreparedPdf({
        keyId: key.id,
        signature,
        serverSignature: attestData.serverMetadata,
        docHash: documentHash,
        publicKey: key.publicKeyArmored,
      });
      setPassphrase("");
    } catch (e) {
      console.error(e);
      alert("Signing failed: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------------------------------
  // Rendering
  // --------------------------------------------------------------------------

  if (step === "success") {
    return (
      <Box
        p={{ base: "md", sm: 40 }}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box style={{ width: "100%", maxWidth: 600, textAlign: "center" }}>
          <Paper p={{ base: "md", sm: "xl" }} withBorder radius="md">
            <Stack align="center" gap="lg">
              <ThemeIcon size={80} radius="xl" color="green" variant="light">
                <span style={{ fontSize: 40 }}>✓</span>
              </ThemeIcon>

              <Title order={2}>Document Prepared &amp; Signed</Title>

              <Text size="lg">
                Your document has been cryptographically signed, locked for
                signing, and downloaded to your device.
              </Text>

              <Paper withBorder p="md" bg="blue.0" radius="sm" w="100%">
                <Text size="sm" c="blue.9" fw={600} mb="xs">
                  🔒 Your document, your rules
                </Text>
                <Text size="sm" c="blue.8">
                  Signed+ never stores your document. It lives on your device
                  and yours alone. Send it however you like, email, messaging,
                  USB stick, cloud storage, or carrier pigeon. Each signer will
                  sign their copy and pass it along.
                </Text>
              </Paper>

              <Divider w="100%" />

              <Group>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (lastDownloadUrl) {
                      const a = document.createElement("a");
                      a.href = lastDownloadUrl;
                      a.download = `${metadata.docTitle || "document"}-prepared.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }
                  }}
                >
                  Download didn't work? Try again
                </Button>
                <Button
                  onClick={() => {
                    // Clean up and reset
                    if (lastDownloadUrl) URL.revokeObjectURL(lastDownloadUrl);
                    setLastDownloadUrl(null);
                    setPdfArrayBuffer(null);
                    setAnnotations([]);
                    setSigners([{ name: "", email: "" }]);
                    setMetadata({ docTitle: "", author: "" });
                    setStep("upload");
                  }}
                >
                  Create Another Document
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (storedKeys.length === 0) {
    return (
      <Center style={{ minHeight: "calc(100vh - 120px)", padding: "2rem" }}>
        <Paper p="xl" radius="lg" withBorder shadow="md" style={{ maxWidth: 500, textAlign: "center", background: "white" }}>
          <ThemeIcon size={72} radius="xl" color="red" variant="light" mb="md">
            <IconShieldLock size={36} />
          </ThemeIcon>
          <Title order={2} mb="sm">Key Vault & Key Setup Required</Title>
          <Text c="dimmed" size="sm" mb="xl">
            To create or prepare documents, you must first initialize your secure key vault and create or import at least one PGP key. Your keys never leave your device.
          </Text>
          <Button 
            size="md" 
            fullWidth 
            onClick={() => {
              if (onNavigate) {
                onNavigate("keys");
              }
            }}
          >
            Go to Key Management
          </Button>
        </Paper>
      </Center>
    );
  }

  if (step === "finalize") {
    return (
      <FinalizeStep
        metadata={metadata}
        numPages={numPages}
        annotations={annotations}
        signers={signers}
        updateSigner={updateSigner}
        saving={saving}
        onBack={() => setStep("editor")}
        onGenerateClick={handleGenerateClick}
        createKeyModalOpen={createKeyModalOpen}
        setCreateKeyModalOpen={setCreateKeyModalOpen}
        handleKeyCreated={handleKeyCreated}
        signingModalOpen={signingModalOpen}
        setSigningModalOpen={setSigningModalOpen}
        availableKeys={availableKeys}
        selectedKeyId={selectedKeyId}
        setSelectedKeyId={setSelectedKeyId}
        passphrase={passphrase}
        setPassphrase={setPassphrase}
        handleConfirmSign={handleConfirmSign}
      />
    );
  }

  if (step === "upload") {
    return (
      <UploadStep
        pdfArrayBuffer={pdfArrayBuffer}
        numPages={numPages}
        metadata={metadata}
        setMetadata={setMetadata}
        loadPdf={loadPdf}
        onClearPdf={() => setPdfArrayBuffer(null)}
        onContinue={handleContinue}
        conflictModalOpen={conflictModalOpen}
        setConflictModalOpen={setConflictModalOpen}
        conflictFile={conflictFile}
        setConflictFile={setConflictFile}
        onNavigate={onNavigate}
      />
    );
  }

  // Editor State
  return (
    <EditorStep
      metadata={metadata}
      currentPage={currentPage}
      numPages={numPages}
      setCurrentPage={setCurrentPage}
      handleStartFinalize={handleStartFinalize}
      pageThumbs={pageThumbs}
      pageImages={pageImages}
      pdfDimensions={pdfDimensions}
      scale={scale}
      scrollViewportRef={scrollViewportRef}
      canvasRef={canvasRef}
      dragImageRef={dragImageRef}
      handleCanvasDrop={handleCanvasDrop}
      annotations={annotations}
      setAnnotations={setAnnotations}
      selectedFieldId={selectedFieldId}
      setSelectedFieldId={setSelectedFieldId}
      editingField={editingField}
      setEditingField={setEditingField}
      signerColors={SIGNER_COLORS}
      activeColor={activeColor}
      selectedSignerIndex={selectedSignerIndex}
      setSelectedSignerIndex={setSelectedSignerIndex}
      onDragStop={onDragStop}
      onResizeStop={onResizeStop}
      updateSignerName={updateSignerName}
      updateSignerEmail={updateSignerEmail}
      removeSigner={removeSigner}
      addSigner={addSigner}
      addField={addField}
      signers={signers}
    />
  );
}
